import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Typography } from "@mui/material";
import AppButton from "../../components/AppButton";
import { GridRowModel } from "@mui/x-data-grid-pro";
import AddIcon from "@mui/icons-material/Add";

import { EditFormDetails } from "../interfaces";
import { InfoButton } from "../../components/Tooltips";

const isOpen = (
  filter: EditFormDetails,
  toggleMap: Record<string, boolean>,
  row?: GridRowModel
): boolean => {
  // Check if the filter has a key and if it exists in the toggleMap
  // toggleMap has priority over others checks
  if (filter.key && toggleMap[filter.key] !== undefined) {
    return toggleMap[filter.key] ?? false;
  }

  if (filter.toggleState) return true;

  // Check conditions based on the row data
  // `toggle_conditions` is a comma-separated string of field names
  // that must be present and non-empty in the row data
  // Every condition must be met for the toggle to be open
  if (row && filter.toggleConditions) {
    const conditions = filter.toggleConditions.split(",");
    for (const condition of conditions) {
      const value = row[condition];
      // If any condition is not met
      if (value === null || value === undefined) return false;
    }
    return true;
  }

  return false;
};

export const TogggleSection = ({
  filter,
  toggleMap,
  setToggleState,
  row,
}: {
  filter: EditFormDetails;
  classes: any;
  toggleMap: Record<string, boolean>;
  setToggleState: (key: string, value: boolean) => void;
  row?: GridRowModel;
}) => {
  const [open, setOpen] = useState<boolean>(isOpen(filter, toggleMap, row));

  useEffect(() => {
    setToggleState(filter.key, open);
  }, [filter.key, open, setToggleState]);

  const label = (
    <FormattedMessage
      id={open && filter.toggleOnLabel ? filter.toggleOnLabel : filter.label}
    />
  );

  // Mirrors FieldGroup's add-row layout (label + small filled "+" button) so
  // toggle sections sit visually consistent with the rest of the dialog.
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
        mt: 1,
        mb: 1.5,
      }}
    >
      <Typography component={"p"}>{label}</Typography>
      {!open ? (
        <AppButton
          intent="tonal"
          sx={{ padding: 0, minWidth: "32px" }}
          onClick={() => setOpen(true)}
        >
          <AddIcon fontSize="small" />
        </AppButton>
      ) : (
        filter.toggleOnTooltip && <InfoButton title={filter.toggleOnTooltip} />
      )}
    </Box>
  );
};
